import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '../lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-body font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:     'bg-brand-green text-white rounded-btn hover:bg-brand-green-dark active:bg-brand-green-dark',
        secondary:   'bg-white border-2 border-brand-green text-brand-green rounded-btn hover:bg-off-white',
        ghost:       'text-brand-green hover:bg-mint/20 rounded-lg',
        destructive: 'bg-error text-white rounded-lg hover:bg-red-700',
        outline:     'border border-border-light bg-white hover:bg-off-white rounded-lg text-text-primary',
        link:        'text-brand-green underline-offset-4 hover:underline p-0 h-auto',
      },
      size: {
        sm:   'h-8 px-4 text-sm min-w-[44px]',
        md:   'h-10 px-6 py-2.5 text-base min-w-[44px]',
        lg:   'h-12 px-8 py-3 text-lg min-w-[44px]',
        icon: 'h-10 w-10 min-w-[44px]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  loadingText?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, loadingText, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {loading && loadingText ? loadingText : children}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
